import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { getDocumentSignedUrl } from "@/lib/storageUtils";
import { MapPin, FileText, Users, Eye, Download, Mail, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { DocumentPreviewDialog } from "@/components/documents/DocumentPreviewDialog";
import DocumentEmailWizard from "@/components/documents/DocumentEmailWizard";

interface SpecialGuest {
  name: string;
  affiliation?: string;
  type?: string;
}

interface MeetingRecord {
  id: string;
  meeting_type: string;
  meeting_date: string;
  meeting_purpose: string | null;
  meeting_location: string | null;
  meeting_start_time: string | null;
  status: 'prep' | 'finalized' | 'archived';
  task_id: string | null;
  expected_attendees: string[] | null;
  actual_attendees: any[] | null;
  special_guests: SpecialGuest[] | null;
  certified_at?: string | null;
  certified_by?: string | null;
}

interface MeetingRecordDetailModalProps {
  meeting: MeetingRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unaId: string;
}

interface Document {
  id: string;
  document_name: string;
  document_type: string;
  status: string;
  file_url: string | null;
  signed_file_url: string | null;
  generated_at: string | null;
}

interface Attendee {
  id: string;
  full_name: string;
  member_role: string;
}

export function MeetingRecordDetailModal({ 
  meeting, 
  open, 
  onOpenChange, 
  unaId 
}: MeetingRecordDetailModalProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [emailDocId, setEmailDocId] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  useEffect(() => {
    if (open && meeting?.task_id) {
      loadMeetingData();
    }
  }, [open, meeting?.task_id]);

  const loadMeetingData = async () => {
    if (!meeting?.task_id) return;
    
    setIsLoading(true);
    try {
      // Fetch documents for this meeting task
      const { data: docs, error: docsError } = await supabase
        .from('generated_documents')
        .select('id, document_name, document_type, status, file_url, signed_file_url, generated_at')
        .eq('task_id', meeting.task_id)
        .order('generated_at', { ascending: true });

      if (docsError) throw docsError;
      setDocuments(docs || []);

      // Fetch attendee details if we have expected_attendees
      if (meeting.expected_attendees && meeting.expected_attendees.length > 0) {
        const { data: attendeeData, error: attendeeError } = await supabase
          .from('una_members')
          .select('id, full_name, member_role')
          .in('id', meeting.expected_attendees);

        if (!attendeeError && attendeeData) {
          setAttendees(attendeeData);
        }
      } else {
        setAttendees([]);
      }
    } catch (error) {
      console.error('Error loading meeting data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!meeting) return null;

  const meetingTypeFormatted = meeting.meeting_type
    ?.replace(/_/g, ' ')
    .replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Meeting';

  const isPrep = meeting.status === 'prep';

  const formatMeetingDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return 'Not specified';
    try {
      // Handle time string format (HH:mm or HH:mm:ss)
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const getDocumentUrl = async (doc: Document) => {
    return await getDocumentSignedUrl({ file_path: doc.signed_file_url || doc.file_url, file_url: doc.file_url });
  };

  const handleDownload = async (doc: Document) => {
    const url = await getDocumentUrl(doc);
    if (!url) return;
    
    setDownloadingDocId(doc.id);
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch document');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const filename = `${doc.document_name?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'document'}.pdf`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      window.open(url, '_blank');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const formatMemberRole = (role: string) => {
    return role
      ?.replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Member';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pr-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle className="text-xl">{meetingTypeFormatted}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {formatMeetingDate(meeting.meeting_date)}
                </p>
              </div>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Recorded Meeting
              </Badge>
            </div>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6 pt-4">
              {/* Meeting Details Section */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-primary" />
                  Meeting Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Date</p>
                    <p className="font-medium">{formatMeetingDate(meeting.meeting_date)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Time</p>
                    <p className="font-medium">{formatTime(meeting.meeting_start_time)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Location</p>
                    <p className="font-medium">{meeting.meeting_location || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Purpose</p>
                    <p className="font-medium">{meeting.meeting_purpose || 'General meeting'}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Attendees Section */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-primary" />
                  Attendees ({attendees.length + (meeting.special_guests?.length || 0)})
                </h3>
                {attendees.length > 0 || (meeting.special_guests && meeting.special_guests.length > 0) ? (
                  <div className="space-y-2">
                    {/* UNA Members */}
                    {attendees.map((attendee) => (
                      <div key={attendee.id} className="flex items-center justify-between text-sm py-1">
                        <span>{attendee.full_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {formatMemberRole(attendee.member_role)}
                        </Badge>
                      </div>
                    ))}
                    {/* External Guests */}
                    {meeting.special_guests && meeting.special_guests.map((guest, index) => (
                      <div key={`guest-${index}`} className="flex items-center justify-between text-sm py-1">
                        <span>{guest.name}</span>
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                          External Guest{guest.affiliation ? ` - ${guest.affiliation}` : ''}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No attendees listed</p>
                )}
              </div>

              <Separator />

              {/* Documents Section */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-primary" />
                  Meeting Documents ({documents.length})
                </h3>
                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <Card key={doc.id} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <p className="font-medium truncate">{doc.document_name}</p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {doc.generated_at && `Generated ${format(parseISO(doc.generated_at), 'MMM d, yyyy')}`}
                              </p>
                            </div>
                            <Badge 
                              variant={doc.status === 'final' || doc.status === 'finalized' ? 'default' : 'secondary'}
                              className="flex-shrink-0"
                            >
                              {doc.status === 'final' || doc.status === 'finalized' ? 'Final' : doc.status}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDocId(doc.id)}
                              className="min-h-[44px] sm:min-h-0"
                            >
                              <Eye className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Preview</span>
                            </Button>
                            {getDocumentUrl(doc) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownload(doc)}
                                disabled={downloadingDocId === doc.id}
                                className="min-h-[44px] sm:min-h-0"
                              >
                                {downloadingDocId === doc.id ? (
                                  <Loader2 className="h-4 w-4 sm:mr-1 animate-spin" />
                                ) : (
                                  <Download className="h-4 w-4 sm:mr-1" />
                                )}
                                <span className="hidden sm:inline">
                                  {downloadingDocId === doc.id ? 'Downloading...' : 'Download'}
                                </span>
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEmailDocId(doc.id)}
                              className="min-h-[44px] sm:min-h-0"
                            >
                              <Mail className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Email</span>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No documents generated yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        documentId={previewDocId}
        open={!!previewDocId}
        onClose={() => setPreviewDocId(null)}
      />

      {/* Document Email Wizard */}
      {emailDocId && (
        <DocumentEmailWizard
          open={!!emailDocId}
          onOpenChange={(open) => !open && setEmailDocId(null)}
          taskId={meeting?.task_id || ''}
          unaId={unaId}
          preSelectedDocumentId={emailDocId}
          onSuccess={() => setEmailDocId(null)}
        />
      )}
    </>
  );
}
